import React, { useState } from 'react';
import { CheckCircle2, ArrowRight, Calendar as CalIcon, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { services, locations, team, clinicInteriorImage, clinicPhone } from '../mock';

const timeSlots = ['8:00 AM', '9:30 AM', '11:00 AM', '1:00 PM', '2:30 PM', '4:00 PM', '5:30 PM'];

const AppointmentPage = () => {
  const [step, setStep] = useState(1);
  const [booking, setBooking] = useState({
    service: services[0].slug,
    clinic: locations[0].slug,
    therapist: team[0].slug,
    date: '',
    time: '',
    name: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [done, setDone] = useState(false);

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = (e) => {
    e.preventDefault();
    try {
      const existing = JSON.parse(localStorage.getItem('uptown_bookings') || '[]');
      existing.push({ ...booking, at: new Date().toISOString() });
      localStorage.setItem('uptown_bookings', JSON.stringify(existing));
    } catch {}
    setDone(true);
  };

  const StepDot = ({ n, label }) => (
    <div className="flex items-center gap-3">
      <span className={`w-8 h-8 rounded-full grid place-items-center text-[13px] font-semibold ${step >= n ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500'}`}>
        {step > n ? <CheckCircle2 size={16} /> : n}
      </span>
      <span className={`text-[13px] ${step >= n ? 'text-neutral-900 font-medium' : 'text-neutral-500'}`}>{label}</span>
    </div>
  );

  return (
    <div className="App bg-white">
      <Header />
      <PageHero
        eyebrow="Call to Book"
        title="Book by"
        italic="phone."
        description="For the fastest appointment booking, please call +1-519-252-2752. You can seek physiotherapy independently, though some benefit plans may require a referral for reimbursement."
        image={clinicInteriorImage}
        breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Call to Book' }]}
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-[880px] mx-auto px-6">
          <div className="mb-10 rounded-3xl border border-[#f5bcc8] bg-[#fff1f3] p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <h2 className="text-[24px] font-bold tracking-tight text-neutral-900">Phone call booking is preferred</h2>
              <p className="mt-2 text-[14px] leading-relaxed text-neutral-700">
                Call the clinic and our reception team will help you choose the right appointment time.
              </p>
            </div>
            <a href="tel:+15192522752" className="inline-flex items-center justify-center gap-2 bg-[#CA0028] hover:bg-[#A80021] text-white text-[15px] font-medium px-6 py-3 rounded-full transition-colors">
              Call {clinicPhone}
            </a>
          </div>

          {/* Stepper */}
          <div className="flex flex-wrap items-center gap-6 gap-y-3 mb-10">
            <StepDot n={1} label="Service & Clinic" />
            <span className="hidden md:block w-10 h-px bg-neutral-200" />
            <StepDot n={2} label="Date & Time" />
            <span className="hidden md:block w-10 h-px bg-neutral-200" />
            <StepDot n={3} label="Your Details" />
          </div>

          {done ? (
            <div className="rounded-3xl border border-[#f5bcc8] bg-[#fff1f3] p-10">
              <div className="w-14 h-14 rounded-full bg-white grid place-items-center">
                <CheckCircle2 size={26} className="text-[#CA0028]" />
              </div>
              <h2 className="mt-6 text-[32px] lg:text-[40px] font-bold tracking-tight text-neutral-900">
                Request received.
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-neutral-700 max-w-lg">
                Thanks {booking.name || 'friend'}. This is an appointment request, not a confirmed booking.
                Please call {clinicPhone} if you need the fastest response or same-day availability.
              </p>
              <div className="mt-8 rounded-2xl bg-white border border-neutral-200 p-6 text-[14px] text-neutral-700 space-y-2">
                <p><span className="font-medium text-neutral-900">Service:</span> {services.find(s => s.slug === booking.service)?.name}</p>
                <p><span className="font-medium text-neutral-900">Clinic:</span> {locations.find(l => l.slug === booking.clinic)?.name}</p>
                <p><span className="font-medium text-neutral-900">Therapist:</span> {team.find(t => t.slug === booking.therapist)?.name}</p>
                <p><span className="font-medium text-neutral-900">When:</span> {booking.date} · {booking.time}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/" className="inline-flex items-center gap-2 bg-[#CA0028] hover:bg-[#A80021] text-white text-[14px] font-medium pl-6 pr-2 py-2 rounded-full transition-colors">
                  Back to Home
                  <span className="w-8 h-8 grid place-items-center rounded-full bg-white/10"><ArrowRight size={14} /></span>
                </Link>
                <Link to="/services" className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-900 text-[14px] font-medium px-5 py-2.5 rounded-full hover:border-[#CA0028] transition-colors">
                  Explore Services
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-neutral-200 p-8 lg:p-10">
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="text-[13px] font-medium text-neutral-700">Which service?</label>
                    <select value={booking.service} onChange={(e) => setBooking({ ...booking, service: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] bg-white focus:border-[#CA0028] outline-none">
                      {services.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-neutral-700">Preferred clinic</label>
                    <div className="mt-2 grid sm:grid-cols-3 gap-3">
                      {locations.map((l) => (
                        <button type="button" key={l.slug} onClick={() => setBooking({ ...booking, clinic: l.slug })} className={`rounded-xl border px-4 py-3 text-left text-[13px] transition-colors ${booking.clinic === l.slug ? 'border-[#CA0028] bg-[#CA0028] text-white' : 'border-neutral-200 hover:border-neutral-400'}`}>
                          <div className="font-medium">{l.name}</div>
                          <div className={`text-[12px] mt-1 ${booking.clinic === l.slug ? 'text-white/70' : 'text-neutral-500'}`}>{l.address.split(',')[1]}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-neutral-700">Preferred therapist (optional)</label>
                    <select value={booking.therapist} onChange={(e) => setBooking({ ...booking, therapist: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] bg-white focus:border-[#CA0028] outline-none">
                      {team.map((t) => <option key={t.slug} value={t.slug}>{t.name} — {t.role}</option>)}
                    </select>
                  </div>
                  <div className="flex justify-end">
                    <button type="button" onClick={next} className="inline-flex items-center gap-2 bg-[#CA0028] hover:bg-[#A80021] text-white text-[14px] font-medium pl-6 pr-2 py-2 rounded-full transition-colors">
                      Continue
                      <span className="w-8 h-8 grid place-items-center rounded-full bg-white/15"><ArrowRight size={14} /></span>
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <label className="text-[13px] font-medium text-neutral-700 flex items-center gap-2"><CalIcon size={14} /> Choose a date</label>
                    <input required type="date" value={booking.date} onChange={(e) => setBooking({ ...booking, date: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] focus:border-[#CA0028] outline-none" />
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-neutral-700 flex items-center gap-2"><Clock size={14} /> Pick a time</label>
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {timeSlots.map((t) => (
                        <button type="button" key={t} onClick={() => setBooking({ ...booking, time: t })} className={`rounded-xl border px-3 py-2 text-[13px] transition-colors ${booking.time === t ? 'border-[#CA0028] bg-[#CA0028] text-white' : 'border-neutral-200 hover:border-neutral-400'}`}>{t}</button>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <button type="button" onClick={back} className="inline-flex items-center gap-2 text-neutral-700 hover:text-neutral-900 text-[14px] font-medium px-4 py-2 transition-colors">Back</button>
                    <button type="button" disabled={!booking.date || !booking.time} onClick={next} className="inline-flex items-center gap-2 bg-[#CA0028] hover:bg-[#A80021] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14px] font-medium pl-6 pr-2 py-2 rounded-full transition-colors">
                      Continue
                      <span className="w-8 h-8 grid place-items-center rounded-full bg-white/15"><ArrowRight size={14} /></span>
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-[13px] font-medium text-neutral-700">Full name</label>
                      <input required value={booking.name} onChange={(e) => setBooking({ ...booking, name: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] focus:border-[#CA0028] outline-none" />
                    </div>
                    <div>
                      <label className="text-[13px] font-medium text-neutral-700">Email</label>
                      <input required type="email" value={booking.email} onChange={(e) => setBooking({ ...booking, email: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] focus:border-[#CA0028] outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-neutral-700">Phone</label>
                    <input required value={booking.phone} onChange={(e) => setBooking({ ...booking, phone: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] focus:border-[#CA0028] outline-none" />
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-neutral-700">Anything we should know?</label>
                    <textarea rows={4} value={booking.notes} onChange={(e) => setBooking({ ...booking, notes: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] focus:border-[#CA0028] outline-none resize-none" placeholder="Injury history, insurance, WSIB claim number, etc." />
                  </div>
                  <div className="flex justify-between">
                    <button type="button" onClick={back} className="inline-flex items-center gap-2 text-neutral-700 hover:text-neutral-900 text-[14px] font-medium px-4 py-2 transition-colors">Back</button>
                    <button type="submit" className="inline-flex items-center gap-2 bg-[#CA0028] hover:bg-[#A80021] text-white text-[14px] font-medium pl-6 pr-2 py-2 rounded-full transition-colors">
                      Send Appointment Request
                      <span className="w-8 h-8 grid place-items-center rounded-full bg-white/10"><ArrowRight size={14} /></span>
                    </button>
                  </div>
                </div>
              )}
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AppointmentPage;
